import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import { Drawer } from '@/components/ui/Drawer'
import { useSession } from '@/features/auth/api/useSession'
import { useCaseLeg } from '@/features/cases/api/useCaseLeg'
import { LegCarriersLine } from '@/features/cases/components/legs/LegCarriersLine'
import { SettlementItem } from '@/features/cases/components/legs/LegFinance'
import { legCarriersOf } from '@/features/cases/components/legs/legCarriers'
import { counterpartyRights } from '@/features/counterparties/counterpartyList'
import { CarrierPaymentSheet } from '@/features/finance/components/CarrierPaymentSheet'
import { useFormat } from '@/i18n/useFormat'
import { CAP, can } from '@/lib/can'
import { formatMoney } from '@/lib/format'
import { modeLabelKey } from '@/lib/icons'
import { unitLabel } from '@/lib/legUnits'
import type { CaseLeg, ExpenseLine, Money } from '@/types/domain'

/* A cost line's leg, from «Деньги» (S-11): the right-edge drawer the line's
 * «Плечо N» cell opens, about that leg's carrier money — and the way to pay the
 * carrier without hunting for the leg page.
 *
 * ── Two overlays, one state, never both ───────────────────────────────────
 *
 * The drawer READS (the line's plan and actual, the leg's outstanding, the
 * per-truck payments) and the payment is the leg page's own
 * `CarrierPaymentSheet` — one form and one mutation, two doors. The two are
 * never open together: «Оплатить перевозчику» closes the drawer and opens the sheet in ONE state change, because the state is one
 * value — nothing, the drawer about a line's leg, or the sheet about a leg —
 * and "both open" is a value it cannot hold.
 *
 * The state is the caller's (`MoneySection` records the pressed line), and
 * this component owns what follows from it: the read, the two overlays and
 * every transition between them.
 *
 * ── Nothing here is computed ──────────────────────────────────────────────
 *
 * The leg's outstanding and its settlements are SERVED on the composed
 * single-leg read (`finance.LegMoneyDTO`); money is printed through
 * `formatMoney`, never added. The carrier's identity follows `legCarriersOf`
 * (AD-6): «Перевозчик скрыт» for a reader who may not see it, and a
 * settlement's counterparty only when the server sent one. Confirming,
 * correcting or rejecting a pending payment stays on the leg page and the
 * case's payments list — this drawer only reads them. */

export type LegPaymentState =
  | null
  /** The drawer, about the leg a cost line is attributed to. */
  | { kind: 'leg'; expense: ExpenseLine; legId: string }
  /** The payment sheet, about a leg read with its money, opened on the unit
   *  of the line it came from when the line names one — and on «Плечо
   *  целиком» when it names none (a leg-level line, S-18a). */
  | { kind: 'pay'; leg: CaseLeg; unitId?: string }

export function CaseLegPaymentDrawer({
  caseId,
  state,
  onStateChange,
}: {
  caseId: string
  state: LegPaymentState
  onStateChange: (next: LegPaymentState) => void
}) {
  const { t } = useTranslation()
  const { data: user } = useSession()

  /* Read only while the drawer is open: the composed single-leg read is the
     one that carries the leg's money block. `cases.leg` hangs under
     `cases.detail`, so a payment's invalidation refreshes it too. */
  const opened = state?.kind === 'leg' ? state : null
  const legRead = useCaseLeg(caseId, opened ? opened.legId : '')
  const leg = opened ? legRead.data : undefined

  /* An affordance, not the gate — the server re-checks the capability (AD-21).
     Without it the button is ABSENT, and so is the sheet. `counterparty.read`
     beside it since S-22d: the payee and the debts are directory
     counterparties, and the server answers 403 `held_by: counterparty.read`
     to a payer who cannot see them. */
  const mayPay = can(user, CAP.financePaymentCarrier) && counterpartyRights(user).read
  const payable = mayPay && leg?.money !== undefined

  const close = () => onStateChange(null)

  return (
    <>
      <Drawer
        open={opened !== null}
        onClose={close}
        title={opened?.expense.description ?? ''}
        size="wide"
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              {t('common.actions.close')}
            </Button>
            {payable && opened && leg ? (
              <Button
                className="flex-1"
                onClick={() =>
                  onStateChange({ kind: 'pay', leg, unitId: opened.expense.unitId })
                }
              >
                {t('cases.legDrawer.pay')}
              </Button>
            ) : null}
          </>
        }
      >
        {opened ? (
          <div className="flex flex-col gap-5 px-4 py-4 sm:px-5">
            {legRead.isPending ? (
              <div
                className="flex flex-col gap-3"
                aria-busy="true"
                aria-label={t('cases.legDrawer.loading')}
              >
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : legRead.isError || !leg ? (
              <ErrorState error={legRead.error} onRetry={() => void legRead.refetch()} />
            ) : (
              <LegMoneyBody
                caseId={caseId}
                leg={leg}
                expense={opened.expense}
                seesAgentIdentity={user?.seesAgentIdentity}
              />
            )}
          </div>
        ) : null}
      </Drawer>

      {/* The leg page's own payment sheet, with what the leg page hands it —
          the leg and its money block. A line on a unit opens it on that
          unit's carrier with the unit ticked; a leg-level line on the leg's
          carrier with «Плечо целиком» ticked. Mounted only in the `pay`
          state: it reads the issuing entities on mount, and nobody else on
          this page needs them. */}
      {state?.kind === 'pay' && state.leg.money ? (
        <CarrierPaymentSheet
          open
          onClose={close}
          caseId={caseId}
          leg={state.leg}
          money={state.leg.money}
          defaultUnitId={state.unitId}
          defaultWholeLeg={state.unitId === undefined}
        />
      ) : null}
    </>
  )
}

/** The drawer's body once the leg has arrived. */
function LegMoneyBody({
  caseId,
  leg,
  expense,
  seesAgentIdentity,
}: {
  caseId: string
  leg: CaseLeg
  expense: ExpenseLine
  seesAgentIdentity: boolean | undefined
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const money = leg.money
  const labelOf = new Map((leg.units ?? []).map((unit) => [unit.id, unitLabel(t, unit)]))
  const carriers = legCarriersOf(leg, seesAgentIdentity)

  return (
    <>
      {/* Which leg, how it moves, and who carries it — one name, «N
          перевозчика» with the names one press away, or «Перевозчик скрыт». */}
      <p className="text-text-muted flex flex-wrap items-baseline gap-x-1.5 gap-y-1 text-[13px] leading-snug">
        <span>{t('cases.legDrawer.leg', { n: leg.sequence, mode: t(modeLabelKey(leg.mode)) })}</span>
        {carriers.kind === 'none' ? null : (
          <>
            <span aria-hidden>·</span>
            <LegCarriersLine summary={carriers} placement="inline" />
          </>
        )}
      </p>

      {/* The LINE's plan and actual, then the LEG's outstanding — labelled as
          the leg's, because it is every line on the leg, not this one. An
          unplanned line's plan is a dash, never the placeholder zero. */}
      <dl className="flex flex-wrap gap-x-7 gap-y-3">
        <Figure
          label={t('cases.legDrawer.planned')}
          value={expense.unplanned ? undefined : expense.planned}
        />
        <Figure label={t('cases.legDrawer.actual')} value={expense.actual} />
        {money ? (
          <Figure label={t('cases.legDrawer.legOutstanding')} value={money.outstanding} />
        ) : null}
      </dl>

      {money ? (
        <section aria-labelledby={`leg-payments-${leg.id}`} className="flex flex-col gap-2">
          <h3
            id={`leg-payments-${leg.id}`}
            className="text-text-heading text-[14px] leading-tight font-normal"
          >
            {t('cases.legDrawer.payments')}
          </h3>
          {money.settlements.length === 0 ? (
            <p className="text-text-muted text-[13px] leading-snug">
              {t('cases.legFinance.noSettlements')}
            </p>
          ) : (
            /* A list, not the leg page's seven-column table: it has to fit a
               phone. What it paid for and how much, then when and which unit. */
            <ul className="flex flex-col">
              {money.settlements.map((settlement) => {
                const unit = settlement.unitId ? labelOf.get(settlement.unitId) : undefined
                return (
                  <li
                    key={settlement.id}
                    className="border-line-hair flex flex-col gap-1 border-b py-2.5 last:border-b-0"
                  >
                    <div className="flex items-start justify-between gap-3 text-[13px]">
                      <div className="text-text-heading min-w-0 [overflow-wrap:anywhere]">
                        <SettlementItem settlement={settlement} />
                      </div>
                      <span className="asa-num text-text-heading shrink-0 text-right whitespace-nowrap">
                        {formatMoney(settlement.amount.amount, settlement.amount.currency)}
                      </span>
                    </div>
                    <span className="text-text-muted text-[12px] leading-snug">
                      {format.dateShort(settlement.receivedOn)}
                      {unit ? (
                        <>
                          {' · '}
                          <span>{unit}</span>
                        </>
                      ) : null}
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      ) : (
        /* No block on the composed read — the server could not assemble it.
           No figure from it is shown, and the leg page is still one press
           away. */
        <p className="text-text-muted text-[13px] leading-snug">{t('cases.legDrawer.noMoney')}</p>
      )}

      <Link
        to={`/cases/${caseId}/legs/${leg.id}`}
        className="text-text-link hover:text-text-link-hover inline-flex items-center gap-1 self-start text-[13px]"
      >
        {t('cases.legDrawer.openLeg', { n: leg.sequence })}
        <ChevronRight size={14} aria-hidden />
      </Link>
    </>
  )
}

/** One figure: its label, then the amount in its own currency — or a dash. */
function Figure({ label, value }: { label: string; value: Money | undefined }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dt className="asa-eyebrow whitespace-nowrap">{label}</dt>
      <dd className="asa-num text-text-heading text-[16px] leading-tight font-semibold whitespace-nowrap">
        {value ? formatMoney(value.amount, value.currency) : '—'}
      </dd>
    </div>
  )
}
